import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"
import { slugify } from "./catalog"

/**
 * Collections on Medusa's product-collection API (products area).
 * Storefront reads: title, handle, metadata.description, metadata.image,
 * metadata.seo_title, metadata.seo_description.
 */

export type Rule = { field: "title" | "tag" | "type" | "vendor" | "price"; op: string; value: string }
export type Rules = { match: "all" | "any"; rules: Rule[] }

export type CollectionDraft = {
  /** null = manual collection; set = automated (products matched by these conditions). */
  rules: Rules | null
  title: string
  handle: string
  description: string
  image: string
  seo_title: string
  seo_description: string
  /** Storefront order: manual | best-selling | title-asc | title-desc | price-asc | price-desc | newest | oldest */
  sort_order: SortOrder
  /** Manual order (product ids), used when sort_order = manual */
  product_order: string[]
  extra_metadata: Record<string, unknown>
}

export type SortOrder = "manual" | "best-selling" | "title-asc" | "title-desc" | "price-asc" | "price-desc" | "newest" | "oldest"
export const SORT_ORDERS: [SortOrder, string][] = [
  ["manual", "Manually"],
  ["best-selling", "Best selling"],
  ["title-asc", "Product title A–Z"],
  ["title-desc", "Product title Z–A"],
  ["price-desc", "Highest price"],
  ["price-asc", "Lowest price"],
  ["newest", "Newest"],
  ["oldest", "Oldest"],
]

export const emptyCollection = (): CollectionDraft => ({ rules: null, title: "", handle: "", description: "", image: "", seo_title: "", seo_description: "", sort_order: "best-selling", product_order: [], extra_metadata: {} })

export function collectionToDraft(c: any): CollectionDraft {
  const { description, image, seo_title, seo_description, terify_rules, sort_order, product_order, ...rest } = (c.metadata ?? {}) as Record<string, unknown>
  const s = (v: unknown) => (v == null ? "" : String(v))
  const so = SORT_ORDERS.some(([k]) => k === sort_order) ? (sort_order as SortOrder) : "newest"
  return {
    rules: (terify_rules as Rules | undefined) ?? null,
    title: c.title ?? "",
    handle: c.handle ?? "",
    description: s(description),
    image: s(image),
    seo_title: s(seo_title),
    seo_description: s(seo_description),
    sort_order: so,
    product_order: Array.isArray(product_order) ? (product_order as unknown[]).filter((x): x is string => typeof x === "string").slice(0, 1000) : [],
    extra_metadata: rest,
  }
}

function payload(d: CollectionDraft) {
  const n = (v: string) => v.trim() || null
  return {
    title: d.title.trim(),
    handle: d.handle.trim() || slugify(d.title),
    metadata: {
      ...d.extra_metadata,
      terify_rules: d.rules,
      description: n(d.description),
      image: n(d.image),
      seo_title: n(d.seo_title),
      seo_description: n(d.seo_description),
      sort_order: d.sort_order,
      product_order: d.sort_order === "manual" && d.product_order.length ? d.product_order : null,
    },
  }
}

export function useCollectionList(p: { q?: string; limit: number; offset: number }) {
  return useQuery({
    queryKey: ["collections", "list", p],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await sdk.admin.productCollection.list({ q: p.q?.trim() || undefined, limit: p.limit, offset: p.offset, order: "title", fields: "id,title,handle,updated_at,+metadata" } as any)
      // Product counts (one light query per row on this page).
      const counts = await Promise.all(
        res.collections.map((c: any) =>
          c.metadata?.terify_rules
            ? sdk.client.fetch<{ count: number }>(`/admin/terify/smart-collections/${c.id}/members`, { query: { limit: 1 } }).then((r) => r.count).catch(() => null)
            : sdk.admin.product.list({ collection_id: [c.id], limit: 1, fields: "id" } as any).then((r) => r.count).catch(() => null)
        )
      )
      return { ...res, collections: res.collections.map((c: any, i: number) => ({ ...c, product_count: counts[i] })) }
    },
  })
}

export function useCollection(id: string | null) {
  return useQuery({
    queryKey: ["collections", "one", id],
    enabled: !!id,
    queryFn: async () => {
      const { collection } = await sdk.admin.productCollection.retrieve(id!, { fields: "id,title,handle,+metadata" } as any)
      return { collection, draft: collectionToDraft(collection) }
    },
  })
}

export function useCollectionProducts(id: string | null, p: { q?: string; limit: number; offset: number }) {
  return useQuery({
    queryKey: ["collections", "products", id, p],
    enabled: !!id,
    placeholderData: keepPreviousData,
    queryFn: () =>
      sdk.admin.product.list({ collection_id: [id!], q: p.q?.trim() || undefined, limit: p.limit, offset: p.offset, order: "title", fields: "id,title,status,thumbnail,+metadata" } as any),
  })
}

/** Every product in a manual collection (for drag ordering), up to 250. */
export function useAllCollectionProducts(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["collections", "all-products", id],
    enabled: !!id && enabled,
    queryFn: async () => (await sdk.admin.product.list({ collection_id: [id!], limit: 250, fields: "id,title,status,thumbnail,+metadata" } as any)).products as any[],
  })
}

export function useSaveCollection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, draft }: { id: string | null; draft: CollectionDraft }) => {
      if (id) return (await sdk.admin.productCollection.update(id, payload(draft))).collection
      return (await sdk.admin.productCollection.create(payload(draft))).collection
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collections"] }),
  })
}

export function useDeleteCollection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => sdk.admin.productCollection.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collections"] }),
  })
}

/** Add/remove products. A product belongs to one collection in Medusa; adding moves it here. */
export function useCollectionMembership(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { add?: string[]; remove?: string[] }) => sdk.admin.productCollection.updateProducts(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections"] })
      qc.invalidateQueries({ queryKey: ["products"] })
    },
  })
}

export const FIELD_LABELS: Record<Rule["field"], string> = { title: "Product title", tag: "Product tag", type: "Product type", vendor: "Product vendor", price: "Price" }
export const TEXT_OP_LABELS: [string, string][] = [["equals", "is equal to"], ["not_equals", "is not equal to"], ["contains", "contains"], ["not_contains", "does not contain"], ["starts_with", "starts with"], ["ends_with", "ends with"]]
export const PRICE_OP_LABELS: [string, string][] = [["equals", "is equal to"], ["greater_than", "is greater than"], ["less_than", "is less than"]]

export function usePreviewRules(rules: Rules | null) {
  const valid = !!rules && rules.rules.length > 0 && rules.rules.every((r) => r.value.trim() !== "")
  return useQuery({
    queryKey: ["collections", "preview", JSON.stringify(rules)],
    enabled: valid,
    placeholderData: keepPreviousData,
    queryFn: () => sdk.client.fetch<{ count: number; products: { id: string; title: string; thumbnail: string | null; status: string }[] }>("/admin/terify/smart-collections/preview", { method: "POST", body: rules! }),
  })
}

export function useAutomatedMembers(id: string | null, p: { q?: string; limit: number; offset: number }) {
  return useQuery({
    queryKey: ["collections", "members", id, p],
    enabled: !!id,
    placeholderData: keepPreviousData,
    queryFn: () => sdk.client.fetch<{ automated: boolean; count: number; products: { id: string; title: string; thumbnail: string | null; status: string }[] }>(`/admin/terify/smart-collections/${id}/members`, { query: { q: p.q?.trim() || undefined, limit: p.limit, offset: p.offset } }),
  })
}
